import { useEffect, useRef, useMemo } from 'react';
import { keepAliveService } from './KeepAliveService';

/**
 * Generate a unique ID for keepalive purposes
 */
const generateKeepAliveId = (): string => {
  return `keepalive_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Custom hook to manage keepalive for long-running processes
 *
 * Automatically starts keepalive when:
 * - isActive is true (processing/training in progress)
 * - Optionally uses provided sessionId, otherwise generates one
 *
 * Automatically stops when:
 * - isActive becomes false
 * - Component unmounts
 *
 * @param sessionIdOrActive - Either a session ID string, or boolean for isActive
 * @param isActive - Whether processing is currently active (optional if first param is boolean)
 *
 * @example
 * ```tsx
 * // With session ID (TrainingPage.tsx)
 * useKeepAlive(trainingState.sessionId, trainingState.isTraining);
 *
 * // Without session ID - auto-generates one (DataProcessingPage.tsx)
 * useKeepAlive(isProcessing);
 *
 * // Or explicitly with null sessionId
 * useKeepAlive(null, isProcessing);
 * ```
 */
export function useKeepAlive(
  sessionIdOrActive: string | null | undefined | boolean,
  isActive?: boolean
): void {
  // Handle overloaded function signature
  const resolvedIsActive = typeof sessionIdOrActive === 'boolean'
    ? sessionIdOrActive
    : (isActive ?? false);

  const providedSessionId = typeof sessionIdOrActive === 'string'
    ? sessionIdOrActive
    : null;

  // Generate a stable ID for this component instance if none provided
  const generatedId = useRef<string | null>(null);

  const effectiveSessionId = useMemo(() => {
    if (providedSessionId) {
      return providedSessionId;
    }
    // Generate ID only once per component lifecycle
    if (!generatedId.current) {
      generatedId.current = generateKeepAliveId();
    }
    return generatedId.current;
  }, [providedSessionId]);

  // Track if we started keepalive so we can clean up properly
  const wasStarted = useRef(false);

  useEffect(() => {
    // Only start if processing is active
    if (resolvedIsActive && effectiveSessionId) {
      keepAliveService.start(effectiveSessionId);
      wasStarted.current = true;
    } else if (wasStarted.current) {
      // Stop if we previously started but conditions no longer met
      keepAliveService.stop();
      wasStarted.current = false;
    }

    // Cleanup on unmount
    return () => {
      if (wasStarted.current) {
        keepAliveService.stop();
        wasStarted.current = false;
      }
    };
  }, [effectiveSessionId, resolvedIsActive]);
}
